/**
 * Um Caso no painel.
 *
 * Só leitura: o cartão leva ao detalhe do Caso e não grava nada (`S11`).
 * Nenhuma transição acontece aqui — quem move o Caso é a tela de Caso, com o
 * gatilho e a confirmação que ela já tem.
 */

import { formatarDataHora } from '../../shared/dominio/datas';
import { Trilhas, WorkCard } from '../../shared/ui/primitivas';
import { etapaDoEstado, rotuloDoEstado } from './etapas';
import { SeloDaSituacaoDoPrazo } from './SeloDaSituacaoDoPrazo';
import { ROTULOS_DO_TIPO_DE_ESPERA, marcaDoVinculoIw, rotuloDoDono } from './api/tipos';
import {
  ROTULO_DO_PACIENTE_PROVISORIO,
  TRILHA_DE_CADASTRO,
  lerRelogio,
  trilhaDoCadastro,
  trilhasDoCaso,
} from './leitura';
import type { CasoDaFila, ResumoDaCompletude } from './api/tipos';

/**
 * A completude verificada (`TP-058`) numa frase: o que falta no cadastro, nomeado, e quantos itens o checklist ainda
 * cobra. A MESMA conta do painel "O que falta para completar" do detalhe.
 */
function textoDaCompletude(completude: ResumoDaCompletude, situacaoDoCadastro: string): string {
  if (completude.nivelDoisCompleto) return 'Checklist completo';
  const cadastro = situacaoDoCadastro === '' ? 'Cadastro completo' : situacaoDoCadastro;
  return `${cadastro} · ${completude.faltam === 1 ? 'falta 1 item' : `faltam ${String(completude.faltam)} itens`} no checklist`;
}

export function CartaoDoCaso({
  caso,
  aoAbrir,
}: {
  caso: CasoDaFila;
  aoAbrir: (casoId: string) => void;
}): React.JSX.Element {
  const relogio = lerRelogio(caso);
  const trilha = trilhaDoCadastro(caso);
  const frentes = trilhasDoCaso(caso.frentes);
  const semEtapa = etapaDoEstado(caso.estado) === 'desconhecida';

  // F4: o Caso fora do mapeamento é o que a flag anuncia, porque é o aviso
  // que manda a pessoa olhar. O provisório cede a vez — ele está no corpo.
  // Quando o vínculo espera alguém (TP-053), a flag diz o que fazer
  // ("Homônimo: escolher") em vez de só "sem vínculo".
  const marcaDoVinculo = marcaDoVinculoIw(caso);
  const flag = semEtapa
    ? ({ label: 'Sem etapa conhecida', tone: 'block' } as const)
    : caso.pacienteProvisorio
      ? ({ label: marcaDoVinculo ?? ROTULO_DO_PACIENTE_PROVISORIO, tone: 'warn' } as const)
      : undefined;

  return (
    <WorkCard
      titulo={caso.pacienteNome ?? caso.pacienteId}
      substatus={rotuloDoEstado(caso.estado)}
      sub={`${caso.nucleoNome ?? 'Sem núcleo'} · ${caso.operadora ?? 'Sem operadora'} · ${rotuloDoDono(caso)}`}
      flag={flag}
      // S6: o destaque de fora do prazo tinge o cartão inteiro, mas a palavra
      // vem junto, no selo e no texto abaixo. Nenhuma informação mora só na cor.
      pendente={relogio.estourado ? 'block' : undefined}
      onClick={() => {
        aoAbrir(caso.id);
      }}
    >
      <span className="anery-ficha-meta">
        {relogio.rotulo}
        {relogio.prazo === null ? null : (
          <>
            {' '}
            {relogio.situacao === null ? null : <SeloDaSituacaoDoPrazo situacao={relogio.situacao} />}{' '}
            <span title={formatarDataHora(relogio.prazo)}>{relogio.texto}</span>
          </>
        )}
        {relogio.prazo === null && relogio.texto !== null ? <> · {relogio.texto}</> : null}
      </span>

      {/* A trilha do cadastro, com dado real: semáforo de quatro estados,
          barra contada em cima dos 5 itens e a frase que NOMEIA o que falta.
          S8 continua atendido — trilha fechada mostra o ✓, que afirma que não
          há pendência em vez de omitir. */}
      {/* As CINCO frentes quando a operadora foi curada (TP-044). Sem curadoria,
          cai para a trilha do cadastro, que é o que existia antes — nenhum Caso
          perde o que já mostrava. */}
      {frentes !== null ? (
        <Trilhas
          trilhas={frentes.trilhas}
          ordem={frentes.ordem}
          rotulos={frentes.rotulos}
          rotulosLongos={frentes.rotulosLongos}
          trava={frentes.trava}
        />
      ) : (
        trilha !== null && (
          <Trilhas
            trilhas={{ [TRILHA_DE_CADASTRO]: trilha }}
            ordem={[TRILHA_DE_CADASTRO]}
            rotulos={{ [TRILHA_DE_CADASTRO]: 'cadastro' }}
            rotulosLongos={{ [TRILHA_DE_CADASTRO]: 'Cadastro do paciente' }}
          />
        )
      )}

      {/* "3 de 5 prontas, travado em Equipe": a frase que o cliente pediu. E
          quando NADA pode andar, a tela diz isso em vez de apontar uma frente
          ao acaso (E7). */}
      {caso.frentes !== null && caso.frentes.curado && (
        <span className="anery-ficha-meta">
          {caso.frentes.prontas} de {caso.frentes.total} prontas
          {caso.frentes.semTravaInterna
            ? ' · nenhuma pode andar: todas esperam outra frente'
            : caso.frentes.travada === null
              ? ''
              : ` · travado em ${frentes?.rotulos[caso.frentes.travada] ?? caso.frentes.travada}`}
          {caso.frentes.tudoDispensado ? ' · todas dispensadas: nenhuma evidência reunida' : ''}
        </span>
      )}

      {/* S8 pede AFIRMAÇÃO, não só o ✓ da barra: o cenário existe para a
          pessoa distinguir "não falta nada" de "o quadro não sabe". A barra
          diz nos dois casos (o `aria-label` carrega "5 de 5 · concluída"),
          mas a frase é o que se lê de relance. */}
      <span className="anery-ficha-meta">
        {caso.completude !== null
          ? textoDaCompletude(caso.completude, trilha?.situacao ?? '')
          : trilha === null
            ? 'Nenhuma pendência registrada'
            : caso.pendenciasAbertas === 0
              ? 'Cadastro completo, sem pendência'
              : trilha.situacao}
      </span>

      {/* E7: nome curto ou só primeiro nome acontece com paciente provisório.
          O cartão mostra o que há; a identificação completa é do detalhe. */}
      {semEtapa && caso.pacienteProvisorio ? (
        <span className="anery-ficha-meta">{marcaDoVinculo ?? ROTULO_DO_PACIENTE_PROVISORIO}</span>
      ) : null}

      {/* Espera por terceiro (TP-048): nada quando vazio, uma etiqueta por tipo aberto. */}
      {caso.espera !== null && caso.espera.abertas.length > 0 ? (
        <span className="anery-ficha-meta">
          {caso.espera.abertas.map((tipo) => ROTULOS_DO_TIPO_DE_ESPERA[tipo]).join(' · ')}
        </span>
      ) : null}
    </WorkCard>
  );
}
